export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function unwrapArray<T = any>(res: any): T[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.AddtionalData)) return res.AddtionalData;
  return [];
}

export function unwrapObject<T = any>(res: any, fallback: T = {} as T): T {
  if (!res) return fallback;
  if (res.data !== undefined) return res.data;
  if (res.AddtionalData !== undefined) return res.AddtionalData;
  return res as T;
}

/** Called when the server ends the session (suspended / deleted account, expired or revoked login) */
let onSessionEnded: ((message: string) => void) | null = null;
export function setSessionEndedHandler(handler: ((message: string) => void) | null) {
  onSessionEnded = handler;
}

async function request<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith("http")
    ? endpoint
    : endpoint.startsWith("/api")
    ? endpoint
    : `/api${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;

  const isFormData = options.body instanceof FormData;
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(!isFormData ? { "Content-Type": "application/json" } : {}),
    ...options.headers,
  };

  const config: RequestInit = {
    ...options,
    headers,
    credentials: "include", // Pass cookies (auth tokens)
  };

  const response = await fetch(url, config);

  let data: any = null;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorMessage =
      (typeof data === "object" && data && (data.message || data.Message || data.error)) ||
      `Request failed with status ${response.status}`;
    // A failed login is also a 401, but it is not the end of an existing session
    if (response.status === 401 && !url.startsWith("/api/auth/login")) onSessionEnded?.(errorMessage);
    throw new ApiError(errorMessage, response.status, data);
  }

  return data as T;
}

export const api = {
  get: <T = any>(url: string, headers?: HeadersInit) =>
    request<T>(url, { method: "GET", headers }),

  post: <T = any>(url: string, body?: any, headers?: HeadersInit) =>
    request<T>(url, {
      method: "POST",
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      headers,
    }),

  upload: <T = any>(url: string, formData: FormData, headers?: HeadersInit) =>
    request<T>(url, {
      method: "POST",
      body: formData,
      headers,
    }),

  put: <T = any>(url: string, body?: any, headers?: HeadersInit) =>
    request<T>(url, {
      method: "PUT",
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      headers,
    }),

  patch: <T = any>(url: string, body?: any, headers?: HeadersInit) =>
    request<T>(url, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      headers,
    }),

  delete: <T = any>(url: string, headers?: HeadersInit) =>
    request<T>(url, { method: "DELETE", headers }),
};
